import { memo, useState } from "react";
import { Input } from "antd";
import {
	AlertTriangle,
	CheckCircle2,
	Eye,
	MessageSquare,
	Pencil,
	Send,
	ShieldCheck,
	ThumbsUp,
	Trash2,
	X
} from "lucide-react";

import { PlanReviewFeedbackDecision, PlanReviewFeedbackErrorAttribution } from "constants/field-values";

/** One plan_review_feedback row, as it arrives nested under the finding. */
export interface PlanReviewFeedbackRow {
	id: string;
	decision?: string | null;
	error_attribution?: string | null;
	comment?: string | null;
	user_id: string;
	created_at: string;
	updated_at: string;
}

export interface PlanReviewFeedbackSubmission {
	decision: PlanReviewFeedbackDecision;
	errorAttribution: PlanReviewFeedbackErrorAttribution | null;
	comment: string;
	/** Set when the reviewer is amending the saved row instead of writing a new one. */
	editingFeedbackId: string | null;
}

export interface PlanReviewFeedbackSectionProps {
	/** The saved row to display, or null to show the empty form. */
	savedFeedback: PlanReviewFeedbackRow | null;
	submitting: boolean;
	deleting: boolean;
	/** Resolves true when the row was persisted, which clears the form. */
	onSubmit: (submission: PlanReviewFeedbackSubmission) => Promise<boolean>;
	onDelete: () => Promise<boolean>;
}

// Three-state rating. One decision + a disambiguation follow-up.
const DECISION_OPTIONS: {
	value: PlanReviewFeedbackDecision;
	label: string;
	icon: typeof CheckCircle2;
	cls: string;
}[] = [
	{
		value: PlanReviewFeedbackDecision.CONFIRMED,
		label: "Confirmed Accurate",
		icon: ThumbsUp,
		cls: "prfs-decision-confirmed"
	},
	{
		value: PlanReviewFeedbackDecision.PARTIALLY_CORRECT,
		label: "Partially Correct",
		icon: Pencil,
		cls: "prfs-decision-partial"
	},
	{ value: PlanReviewFeedbackDecision.INCORRECT, label: "Incorrect", icon: X, cls: "prfs-decision-incorrect" }
];

// Disambiguation: what the reviewer picks becomes error_attribution, which is what makes
// per-model accuracy queryable (reading the plans vs. applying the requirement).
const ATTRIBUTION_OPTIONS: { value: PlanReviewFeedbackErrorAttribution; label: string; icon: typeof CheckCircle2 }[] = [
	{ value: PlanReviewFeedbackErrorAttribution.PERCEPTION, label: "Observation was wrong", icon: Eye },
	{ value: PlanReviewFeedbackErrorAttribution.REASONING, label: "Compliance call was wrong", icon: ShieldCheck },
	{ value: PlanReviewFeedbackErrorAttribution.BOTH, label: "Both", icon: AlertTriangle }
];

const humanize = (raw?: string | null): string =>
	raw
		? raw
				.replace(/_/g, " ")
				.toLowerCase()
				.replace(/\b\w/g, c => c.toUpperCase())
		: "";

const formatDate = (iso?: string | null): string => (iso ? new Date(iso).toLocaleString() : "—");

const decisionLabel = (decision?: string | null): string | null =>
	decision ? (DECISION_OPTIONS.find(option => option.value === decision)?.label ?? humanize(decision)) : null;

const attributionLabel = (attribution?: string | null): string | null =>
	attribution
		? (ATTRIBUTION_OPTIONS.find(option => option.value === attribution)?.label ?? humanize(attribution))
		: null;

const decisionTagClass = (decision?: string | null): string => {
	if (decision === PlanReviewFeedbackDecision.CONFIRMED) return "prfs-history-tag-up";
	if (decision === PlanReviewFeedbackDecision.PARTIALLY_CORRECT) return "prfs-history-tag-warn";
	return "prfs-history-tag-down";
};

// Icon + colour for the saved attribution chip. One subsystem off (perception OR
// reasoning) = warning amber; both off = fail red.
const attributionVisual = (attribution?: string | null): { icon: typeof CheckCircle2; cls: string } => {
	switch (attribution) {
		case PlanReviewFeedbackErrorAttribution.PERCEPTION:
			return { icon: Eye, cls: "prfs-history-attr-warning" };
		case PlanReviewFeedbackErrorAttribution.REASONING:
			return { icon: ShieldCheck, cls: "prfs-history-attr-warning" };
		case PlanReviewFeedbackErrorAttribution.BOTH:
			return { icon: AlertTriangle, cls: "prfs-history-attr-fail" };
		default:
			return { icon: CheckCircle2, cls: "prfs-history-attr-pass" };
	}
};

/**
 * "Reviewer Feedback" panel inside the plan review finding modal: a three-state decision
 * plus, on the non-confirming paths, which part of the analysis was off and why.
 *
 * Owns only the form; the modal owns the mutations. Form state is per-mount, so the modal
 * re-keys the panel per finding to reset it.
 */
const PlanReviewFeedbackSection = ({
	savedFeedback,
	submitting,
	deleting,
	onSubmit,
	onDelete
}: PlanReviewFeedbackSectionProps) => {
	const [decision, setDecision] = useState<PlanReviewFeedbackDecision | null>(null);
	const [errorAttribution, setErrorAttribution] = useState<PlanReviewFeedbackErrorAttribution | null>(null);
	const [comment, setComment] = useState("");
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [isEditing, setIsEditing] = useState(false);
	// True only after a successful save in THIS panel — drives the ephemeral "Feedback
	// received" banner, so it never shows just because saved feedback already exists.
	const [justSubmitted, setJustSubmitted] = useState(false);

	const resetForm = () => {
		setDecision(null);
		setErrorAttribution(null);
		setComment("");
		setSubmitError(null);
		setIsEditing(false);
	};

	// Re-open the form pre-filled with the saved feedback so the reviewer can amend it.
	const handleStartEdit = () => {
		if (!savedFeedback) return;
		setDecision((savedFeedback.decision as PlanReviewFeedbackDecision) ?? null);
		setErrorAttribution((savedFeedback.error_attribution as PlanReviewFeedbackErrorAttribution) ?? null);
		setComment(savedFeedback.comment ?? "");
		setSubmitError(null);
		setIsEditing(true);
	};

	const handleSubmit = async () => {
		if (decision === null) return;
		const needsAttribution = decision !== PlanReviewFeedbackDecision.CONFIRMED;
		// On the non-confirming paths, the reviewer must either pick which part was off or
		// explain in a comment (the "can't pick a clean attribution" escape hatch).
		if (needsAttribution && errorAttribution === null && comment.trim().length === 0) {
			setSubmitError("Tell Ollie which part was off, or add a short comment.");
			return;
		}
		setSubmitError(null);

		const saved = await onSubmit({
			decision,
			errorAttribution: needsAttribution ? errorAttribution : null,
			comment: comment.trim(),
			editingFeedbackId: isEditing && savedFeedback ? savedFeedback.id : null
		});
		if (!saved) return;

		resetForm();
		setJustSubmitted(true);
	};

	const handleDelete = async () => {
		const removed = await onDelete();
		if (removed) resetForm();
	};

	return (
		<div className="prfs-section">
			<div className="prfs-header">
				<div className="prfs-header-icon-container">
					<MessageSquare className="prfs-header-icon" />
				</div>
				<div>
					<span className="prfs-header-title">Reviewer Feedback</span>
					<p className="prfs-header-subtitle">Is this AI analysis accurate?</p>
				</div>
			</div>

			{savedFeedback && !isEditing ? (
				/* ── Saved feedback: green confirmation + edit / delete ─────────────── */
				<div className="prfs-received">
					{/* Ephemeral: shown only right after a successful save; gone on reopen. */}
					{justSubmitted && (
						<div className="prfs-received-banner">
							<CheckCircle2 className="prfs-received-icon" />
							<div>
								<p className="prfs-received-title">Feedback received</p>
								<p className="prfs-received-subtitle">Thank you for helping improve to Ollie.</p>
							</div>
						</div>
					)}

					{(() => {
						const attribution = savedFeedback.error_attribution ?? null;
						const attr = attributionLabel(attribution);
						const attrVisual = attributionVisual(attribution);
						const AttrIcon = attrVisual.icon;
						return (
							<div className="prfs-saved">
								<div className="prfs-history-head">
									<span className={`prfs-history-tag ${decisionTagClass(savedFeedback.decision)}`}>
										{decisionLabel(savedFeedback.decision) ?? "Feedback"}
									</span>
									{attr && (
										<span className={`prfs-history-attr ${attrVisual.cls}`}>
											<AttrIcon className="prfs-history-attr-icon" />
											{attr}
										</span>
									)}
									{savedFeedback.updated_at && <p>{formatDate(savedFeedback.updated_at)}</p>}
								</div>
								{savedFeedback.comment && <p className="prfs-history-comment">{savedFeedback.comment}</p>}
							</div>
						);
					})()}

					<div className="prfs-submit-row">
						<button className="prfs-cancel-button" onClick={handleStartEdit}>
							<Pencil size={14} /> Edit
						</button>
						<button className="prfs-delete-button" onClick={handleDelete} disabled={deleting}>
							<Trash2 size={14} /> {deleting ? "Deleting…" : "Delete"}
						</button>
					</div>
				</div>
			) : (
				/* ── Feedback form (create or edit) ─────────────────────────────────── */
				<>
					<div className="prfs-decision-row">
						{DECISION_OPTIONS.map(option => {
							const DecisionIcon = option.icon;
							const isActive = decision === option.value;
							return (
								<button
									key={option.value}
									type="button"
									className={`prfs-decision-btn ${option.cls} ${isActive ? "prfs-decision-btn-active" : ""}`}
									onClick={() => {
										setDecision(option.value);
										if (option.value === PlanReviewFeedbackDecision.CONFIRMED) setErrorAttribution(null);
										setSubmitError(null);
									}}
								>
									<DecisionIcon className="prfs-decision-btn-icon" />
									{option.label}
								</button>
							);
						})}
					</div>

					{/* Disambiguation + comment — only on the two non-confirming paths. */}
					{decision && decision !== PlanReviewFeedbackDecision.CONFIRMED && (
						<>
							<div className="prfs-attribution">
								<p className="prfs-attribution-label">Which part was off?</p>
								<div className="prfs-attribution-options">
									{ATTRIBUTION_OPTIONS.map(option => {
										const AttributionIcon = option.icon;
										const isActive = errorAttribution === option.value;
										return (
											<button
												key={option.value}
												type="button"
												className={`prfs-attribution-btn ${isActive ? "prfs-attribution-btn-active" : ""}`}
												onClick={() => {
													setErrorAttribution(isActive ? null : option.value);
													setSubmitError(null);
												}}
											>
												<AttributionIcon className="prfs-attribution-btn-icon" />
												{option.label}
											</button>
										);
									})}
								</div>
							</div>

							<p className="prfs-attribution-label">
								{decision === PlanReviewFeedbackDecision.INCORRECT
									? "Reason for incorrect analysis (Optional)"
									: "What's missing or slightly off? (Optional)"}
							</p>
							<Input.TextArea
								className="prfs-comment"
								rows={3}
								placeholder={"Please explain why the AI analysis is incorrect to help us improve..."}
								value={comment}
								onChange={e => setComment(e.target.value)}
							/>
						</>
					)}

					{decision && (
						<>
							{submitError && <p className="prfs-error">{submitError}</p>}
							<div className="prfs-submit-row">
								<button className="prfs-cancel-button" onClick={resetForm}>
									Cancel
								</button>
								<button className="prfs-submit-button" onClick={handleSubmit} disabled={submitting}>
									<Send size={16} color="white" strokeWidth={1.33} />{" "}
									{submitting ? "Saving…" : isEditing ? "Save changes" : "Submit feedback"}
								</button>
							</div>
						</>
					)}
				</>
			)}
		</div>
	);
};

export default memo(PlanReviewFeedbackSection);
